(function () {
  'use strict';

  const ASSET_HOST = 'prod-hub-config.8ballpool.com';
  const RUNTIME_URL = 'https://8ballpool.com/scripts/spine-3.7/spine-player.js';
  const RUNTIME_CSS_URL = 'https://8ballpool.com/scripts/spine-3.7/spine-player.css';
  const records = new Map();
  const atlasRequests = new Map();
  let runtimePromise;

  function isTrustedAsset(url, extension) {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:'
        && parsed.hostname === ASSET_HOST
        && parsed.pathname.toLowerCase().endsWith(extension);
    } catch (_) {
      return false;
    }
  }

  function loadRuntimeStyles() {
    if (document.querySelector('[data-8bp-spine-runtime-styles]')) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = RUNTIME_CSS_URL;
    link.setAttribute('data-8bp-spine-runtime-styles', '');
    document.head.appendChild(link);
  }

  function loadRuntime() {
    if (window.spine?.SpinePlayer) return Promise.resolve();
    if (runtimePromise) return runtimePromise;

    loadRuntimeStyles();
    runtimePromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('[data-8bp-spine-runtime]');
      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = RUNTIME_URL;
      script.async = true;
      script.setAttribute('data-8bp-spine-runtime', '');
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return runtimePromise;
  }

  function getAtlasText(url, signal) {
    if (!atlasRequests.has(url)) {
      atlasRequests.set(url, fetch(url).then((response) => {
        if (!response.ok) throw new Error(`Atlas request failed (${response.status})`);
        return response.text();
      }).catch((error) => {
        atlasRequests.delete(url);
        throw error;
      }));
    }

    if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
    return atlasRequests.get(url);
  }

  function dispose(container) {
    visibilityObserver?.unobserve(container);
    const record = records.get(container);
    if (!record) return;

    record.controller.abort();
    if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
    if (record.player && !record.player.disposed) {
      try {
        record.player.dispose();
      } catch (_) {
        // The detached fallback image remains usable even if runtime cleanup fails.
      }
    }
    records.delete(container);
    container.classList.remove('is-loaded');
    delete container.dataset.animationObserved;
  }

  async function initialize(container) {
    if (records.has(container) || container.dataset.animationFailed === 'true') return;

    const jsonUrl = container.dataset.jsonUrl;
    const atlasUrl = container.dataset.atlasUrl;
    const pngUrl = container.dataset.pngUrl;
    if (
      !isTrustedAsset(jsonUrl, '.json')
      || !isTrustedAsset(atlasUrl, '.atlas')
      || !isTrustedAsset(pngUrl, '.png')
    ) {
      container.dataset.animationFailed = 'true';
      return;
    }

    const record = {
      atlasObjectUrl: null,
      container,
      controller: new AbortController(),
      player: null,
      visible: true
    };
    records.set(container, record);

    try {
      const [, atlasText] = await Promise.all([
        loadRuntime(),
        getAtlasText(atlasUrl, record.controller.signal)
      ]);
      if (record.controller.signal.aborted || !container.isConnected) return;

      const atlasLines = atlasText.split(/\r?\n/);
      const pageLine = atlasLines.findIndex((line) => line.trim());
      if (pageLine < 0) throw new Error('Animation atlas is empty');

      const pngFileName = new URL(pngUrl).pathname.split('/').pop();
      atlasLines[pageLine] = pngFileName;
      record.atlasObjectUrl = URL.createObjectURL(new Blob([atlasLines.join('\n')], { type: 'text/plain' }));

      let player;
      player = new window.spine.SpinePlayer(container, {
        jsonUrl,
        atlasUrl: record.atlasObjectUrl,
        pageBaseUrl: pngUrl.slice(0, pngUrl.lastIndexOf('/')),
        animation: 'animation',
        alpha: true,
        backgroundColor: '#00000000',
        showControls: false,
        success() {
          record.player = player;
          if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
          record.atlasObjectUrl = null;

          if (!container.isConnected) {
            dispose(container);
            return;
          }

          container.classList.add('is-loaded');
          player.paused = document.hidden || !record.visible;
        },
        error(_player, reason) {
          if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
          record.atlasObjectUrl = null;
          records.delete(container);
          container.dataset.animationFailed = 'true';
          console.warn('8BP animation could not be loaded; using the static reward image.', reason);
        }
      });
      record.player = player;
    } catch (error) {
      if (record.atlasObjectUrl) URL.revokeObjectURL(record.atlasObjectUrl);
      records.delete(container);
      if (error.name === 'AbortError') return;
      container.dataset.animationFailed = 'true';
      console.warn('8BP animation could not be initialized; using the static reward image.', error);
    }
  }

  const visibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const record = records.get(entry.target);
        if (!record && entry.isIntersecting) {
          initialize(entry.target);
          return;
        }

        if (!record) return;
        record.visible = entry.isIntersecting;
        if (record.player) record.player.paused = document.hidden || !entry.isIntersecting;
      });
    }, { rootMargin: '160px', threshold: 0.01 })
    : null;

  function observe(container) {
    if (container.dataset.animationObserved === 'true') return;
    container.dataset.animationObserved = 'true';
    if (visibilityObserver) {
      visibilityObserver.observe(container);
    } else {
      initialize(container);
    }
  }

  class EightBpAnimation extends HTMLElement {
    connectedCallback() {
      observe(this);
    }

    disconnectedCallback() {
      dispose(this);
    }
  }

  class EightBpCardSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.deal-card-media__track');
      this.pageCount = Number(this.dataset.pageCount) || 1;
      this.pageIndex = 0;
      if (!this.track || this.pageCount < 2) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverTarget = this.closest('.deal-card-media') || this;
      this.start = this.start.bind(this);
      this.stop = this.stop.bind(this);
      this.hoverTarget.addEventListener('mouseenter', this.start);
      this.hoverTarget.addEventListener('mouseleave', this.stop);
    }

    disconnectedCallback() {
      this.stop(false);
      this.hoverTarget?.removeEventListener('mouseenter', this.start);
      this.hoverTarget?.removeEventListener('mouseleave', this.stop);
    }

    showPage(index) {
      this.pageIndex = index;
      this.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
    }

    start() {
      if (!window.matchMedia('(hover: hover)').matches) return;
      this.stop(false);
      this.showPage((this.pageIndex + 1) % this.pageCount);
      if (this.reducedMotion.matches) return;
      this.advanceTimer = window.setInterval(() => {
        this.showPage((this.pageIndex + 1) % this.pageCount);
      }, 1400);
    }

    stop(reset = true) {
      if (this.advanceTimer) window.clearInterval(this.advanceTimer);
      this.advanceTimer = null;
      if (reset && this.track) this.showPage(0);
    }
  }

  const VIP_STORAGE_KEY = 'bbirdg:8bp-vip-user-id:v1';
  const VIP_RECENT_STORAGE_KEY = 'bbirdg:8bp-vip-recent-id:v1';
  const VIP_PROFILE_STORAGE_KEY = 'bbirdg:8bp-vip-profile:v1';
  const VIP_RECENT_LIMIT = 3;
  const VIP_PROFILE_TTL = 60 * 60 * 1000;
  const VIP_HIGHLIGHT_DURATION = 1800;
  const VIP_API_URL = 'https://vip-calc-306467741271.europe-west1.run.app/calculate-rewards';
  const VIP_CARD_TITLE_SELECTOR = '.prd-name a, .minicart-prd-name a, .prd-prw-title a';
  const vipCardOriginalTitles = new WeakMap();
  const vipHighlightTimers = new WeakMap();
  let vipProfileRequest = null;
  let activeVipCardProfile = null;

  function readStoredVipId() {
    try {
      const value = window.localStorage.getItem(VIP_STORAGE_KEY)?.trim() || '';
      return /^\d+$/.test(value) ? value : '';
    } catch (_) {
      return '';
    }
  }

  function normalizeVipProfile(data, userId) {
    const multipliers = data?.multipliers;
    if (!multipliers || !['cash', 'coins', 'vip_points'].every((key) => Number(multipliers[key]) > 0)) return null;
    return {
      userId: String(userId),
      username: String(data.username || ''),
      vipTier: String(data.vip_tier || ''),
      multipliers: {
        cash: Number(multipliers.cash),
        coins: Number(multipliers.coins),
        vip_points: Number(multipliers.vip_points)
      },
      verifiedAt: Date.now()
    };
  }

  function readVipProfile(userId = readStoredVipId()) {
    if (!userId) return null;
    try {
      const profile = JSON.parse(window.localStorage.getItem(VIP_PROFILE_STORAGE_KEY) || 'null');
      const verifiedAt = Number(profile?.verifiedAt);
      if (!profile || profile.userId !== userId || !Number.isFinite(verifiedAt) || Date.now() - verifiedAt > VIP_PROFILE_TTL) return null;
      return normalizeVipProfile({
        username: profile.username,
        vip_tier: profile.vipTier,
        multipliers: profile.multipliers
      }, userId) && profile;
    } catch (_) {
      return null;
    }
  }

  function storeVipProfile(data, userId) {
    const profile = normalizeVipProfile(data, userId);
    if (!profile) return null;
    try { window.localStorage.setItem(VIP_PROFILE_STORAGE_KEY, JSON.stringify(profile)); } catch (_) {}
    document.dispatchEvent(new CustomEvent('eight-bp:vip-profile-changed', { detail: { profile } }));
    return profile;
  }

  function clearVipProfile() {
    try { window.localStorage.removeItem(VIP_PROFILE_STORAGE_KEY); } catch (_) {}
    document.dispatchEvent(new CustomEvent('eight-bp:vip-profile-changed', { detail: { profile: null } }));
  }

  async function requestVipProfile(userId) {
    if (vipProfileRequest?.userId === userId) return vipProfileRequest.promise;
    const promise = fetch(VIP_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, base_cash: 1, base_coins: 1, base_vip_points: 1 })
    }).then(async (response) => {
      let data = null;
      try { data = await response.json(); } catch (_) { data = null; }
      if (!response.ok || !data) throw new Error('VIP profile lookup failed.');
      const profile = storeVipProfile(data, userId);
      if (!profile) throw new Error('VIP profile response was invalid.');
      return profile;
    }).finally(() => {
      if (vipProfileRequest?.promise === promise) vipProfileRequest = null;
    });
    vipProfileRequest = { userId, promise };
    return promise;
  }

  function roundHalfToEven(value) {
    const lower = Math.floor(value);
    const fraction = value - lower;
    const tolerance = Number.EPSILON * Math.max(1, Math.abs(value)) * 4;
    if (Math.abs(fraction - 0.5) <= tolerance) return lower % 2 === 0 ? lower : lower + 1;
    return Math.round(value);
  }

  function vipRewardLabel(type) {
    if (type === 'cash') return 'Cash';
    if (type === 'coins') return 'Coins';
    if (type === 'vip_points') return 'VIP Points';
    return '';
  }

  function replaceVipTitleAmount(title, type, amount) {
    const label = vipRewardLabel(type);
    if (!label || !Number.isFinite(Number(amount))) return title;
    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return String(title).replace(
      new RegExp(`[\\d,.]+(?=\\s+${escapedLabel}\\b)`, 'i'),
      Math.round(Number(amount)).toLocaleString('en-US')
    );
  }

  function animateVipHighlightLayer(element, role) {
    if (!element) return;
    element.classList.add('vip-highlight-layer');
    element.dataset.vipHighlightRole = role;
    const currentTimer = vipHighlightTimers.get(element);
    if (currentTimer) window.clearTimeout(currentTimer);
    element.classList.remove('is-vip-highlight-animating');
    void element.offsetWidth;
    element.classList.add('is-vip-highlight-animating');
    vipHighlightTimers.set(element, window.setTimeout(() => {
      element.classList.remove('is-vip-highlight-animating');
      element.classList.remove('vip-highlight-layer');
      delete element.dataset.vipHighlightRole;
      vipHighlightTimers.delete(element);
    }, VIP_HIGHLIGHT_DURATION));
  }

  function highlightVipValue(element, animate = true) {
    if (!element) return;
    element.classList.add('vip-value-highlight', 'is-vip-highlighted');
    if (!animate) return;
    animateVipHighlightLayer(element, 'text');

    const badge = element.closest('.deal-card-media__amount, .deal-bundle__amount, .bundle-detail__amount');
    if (!badge) return;
    animateVipHighlightLayer(badge, 'badge');

    const card = badge.closest('.deal-card-media__item, .deal-bundle__item, .bundle-detail');
    if (!card) return;
    animateVipHighlightLayer(card, 'card');

    card.querySelectorAll('.deal-card-media__image, .deal-card-media__spine, .deal-bundle__art, .bundle-detail__art')
      .forEach((art) => animateVipHighlightLayer(art, 'art'));
  }

  function renderVipNumericValue(element, { prefix = '', value = '', suffix = '', highlighted = false, animate = false } = {}) {
    if (!element) return;
    const nodes = [document.createTextNode(prefix)];
    if (highlighted) {
      const number = document.createElement('span');
      number.textContent = value;
      nodes.push(number);
      element.replaceChildren(...nodes, document.createTextNode(suffix));
      highlightVipValue(number, animate);
    } else {
      element.replaceChildren(...nodes, document.createTextNode(value), document.createTextNode(suffix));
    }
  }

  function renderVipTitleAmount(title, sourceTitle, type, amount, animate = false) {
    const updatedTitle = replaceVipTitleAmount(sourceTitle, type, amount);
    const label = vipRewardLabel(type);
    if (updatedTitle === sourceTitle || !label) return false;
    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = updatedTitle.match(new RegExp(`[\\d,.]+(?=\\s+${escapedLabel}\\b)`, 'i'));
    if (!match || match.index === undefined) return false;
    const value = document.createElement('span');
    value.textContent = match[0];
    title.replaceChildren(
      document.createTextNode(updatedTitle.slice(0, match.index)),
      value,
      document.createTextNode(updatedTitle.slice(match.index + match[0].length))
    );
    highlightVipValue(value, animate);
    return true;
  }

  function cardRootsWithin(scope = document) {
    const roots = new Set();
    if (scope instanceof Element) {
      const parentRoot = scope.closest('[data-vip-card-title]');
      if (parentRoot) roots.add(parentRoot);
    }
    scope.querySelectorAll?.('[data-vip-card-title]').forEach((root) => roots.add(root));
    return roots;
  }

  function restoreVipCardTitles(scope = document) {
    cardRootsWithin(scope).forEach((root) => {
      root.querySelectorAll(VIP_CARD_TITLE_SELECTOR).forEach((title) => {
        const original = vipCardOriginalTitles.get(title);
        if (original !== undefined) title.innerHTML = original;
      });
    });
  }

  function applyVipCardTitles(profile, scope = document, animate = false) {
    if (!profile) return restoreVipCardTitles(scope);
    cardRootsWithin(scope).forEach((root) => {
      const type = root.dataset.vipTitleReward;
      const baseAmount = Number(root.dataset.vipBaseAmount);
      const multiplier = Number(profile.multipliers?.[type]);
      if (!type || !(baseAmount > 0) || !(multiplier > 0)) return;
      const adjustedAmount = roundHalfToEven(baseAmount * multiplier);
      root.querySelectorAll(VIP_CARD_TITLE_SELECTOR).forEach((title) => {
        if (!vipCardOriginalTitles.has(title)) vipCardOriginalTitles.set(title, title.innerHTML);
        renderVipTitleAmount(title, title.textContent.trim(), type, adjustedAmount, animate);
      });
    });
  }

  function initializeVipCardTitles() {
    if (!document.querySelector('[data-vip-card-title]')) return;
    const userId = readStoredVipId();
    if (!userId) return restoreVipCardTitles();
    const cached = readVipProfile(userId);
    if (cached) {
      activeVipCardProfile = cached;
      applyVipCardTitles(cached, document, true);
    }
    else requestVipProfile(userId).catch((error) => console.error('Theme JS: VIP profile lookup error:', error));
  }

  const vipCardObserver = new MutationObserver((mutations) => {
    if (!activeVipCardProfile) return;
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) applyVipCardTitles(activeVipCardProfile, node);
    }));
  });

  document.addEventListener('eight-bp:vip-profile-changed', (event) => {
    activeVipCardProfile = event.detail?.profile || null;
    applyVipCardTitles(activeVipCardProfile, document, true);
  });
  document.addEventListener('eight-bp:vip-id-changed', (event) => {
    const userId = String(event.detail?.userId || '').trim();
    if (!userId) {
      clearVipProfile();
      return;
    }
    const cached = readVipProfile(userId);
    if (cached) {
      activeVipCardProfile = cached;
      applyVipCardTitles(cached, document, true);
    }
    else if (document.querySelector('[data-vip-card-title]')) {
      requestVipProfile(userId).catch((error) => console.error('Theme JS: VIP profile lookup error:', error));
    }
  });
  window.addEventListener('storage', (event) => {
    if (event.key === VIP_PROFILE_STORAGE_KEY) {
      activeVipCardProfile = readVipProfile();
      applyVipCardTitles(activeVipCardProfile, document, true);
    }
    else if (event.key === VIP_STORAGE_KEY) {
      if (event.newValue) initializeVipCardTitles();
      else {
        activeVipCardProfile = null;
        restoreVipCardTitles();
      }
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeVipCardTitles, { once: true });
  else initializeVipCardTitles();
  vipCardObserver.observe(document.documentElement, { childList: true, subtree: true });

  class EightBpVipCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.vipCalculatorReady === 'true') return;
      this.dataset.vipCalculatorReady = 'true';
      this.rewardScope = this.closest('eight-bp-bundle-details');
      this.descriptionScope = document.querySelector(this.dataset.descriptionScope) || this.closest('[data-section-render]') || document;
      this.userIdInput = this.querySelector('[data-vip-user-id]');
      this.mainButton = this.querySelector('.calc-button');
      this.loading = this.querySelector('[data-vip-loading]');
      this.error = this.querySelector('[data-vip-error]');
      this.result = this.querySelector('[data-vip-result]');
      this.recent = this.querySelector('[data-vip-recent-id]');
      this.recentItems = this.querySelector('[data-vip-recent-items]');
      this.helpButton = this.querySelector('[data-vip-help]');
      this.helpTooltip = null;
      this.translations = this.readTranslations();
      this.originalValues = null;
      this.activeUserId = '';
      this.requestSequence = 0;
      this.activeModal = null;
      this.modalPlaceholder = null;
      this.modalTrigger = null;
      this.productTitle = this.descriptionScope.querySelector('[data-vip-product-title]')
        || (this.dataset.dynamicTitleReward ? this.descriptionScope.querySelector('.prd-block-name') : null);
      this.originalProductTitle = this.productTitle?.textContent.trim() || '';

      if (!this.userIdInput || !this.mainButton || !this.loading || !this.error || !this.result) return;

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onBlur = this.onBlur.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onExternalId = this.onExternalId.bind(this);
      this.onStorage = this.onStorage.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDocumentReady = this.initializeHelpTooltip.bind(this);
      this.addEventListener('click', this.onClick);
      this.userIdInput.addEventListener('input', this.onInput);
      this.userIdInput.addEventListener('focus', this.onFocus);
      this.userIdInput.addEventListener('click', this.onFocus);
      this.userIdInput.addEventListener('blur', this.onBlur);
      this.querySelector('form')?.addEventListener('submit', this.onSubmit);
      document.addEventListener('eight-bp:vip-id-changed', this.onExternalId);
      window.addEventListener('storage', this.onStorage);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeyDown, true);
      this.initializeHelpTooltip();
      this.initialize();
    }

    disconnectedCallback() {
      this.closeModal(false);
      this.removeEventListener('click', this.onClick);
      this.userIdInput?.removeEventListener('input', this.onInput);
      this.userIdInput?.removeEventListener('focus', this.onFocus);
      this.userIdInput?.removeEventListener('click', this.onFocus);
      this.userIdInput?.removeEventListener('blur', this.onBlur);
      this.querySelector('form')?.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('eight-bp:vip-id-changed', this.onExternalId);
      window.removeEventListener('storage', this.onStorage);
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeyDown, true);
      document.removeEventListener('DOMContentLoaded', this.onDocumentReady);
      this.helpTooltip?.destroy();
    }

    readTranslations() {
      try {
        return JSON.parse(this.querySelector('[data-vip-calculator-translations]')?.textContent || '{}');
      } catch (error) {
        console.error('Theme JS: Invalid 8BP VIP calculator translations.', error);
        return {};
      }
    }

    initializeHelpTooltip() {
      const content = this.helpButton?.dataset.vipHelp;
      if (!content || this.helpTooltip) return;
      if (typeof window.tippy !== 'function') {
        document.addEventListener('DOMContentLoaded', this.onDocumentReady, { once: true });
        return;
      }
      this.helpTooltip = window.tippy(this.helpButton, {
        content,
        theme: document.body.dataset.tooltipColor,
        animation: document.body.dataset.tooltipAnimation,
        duration: [document.body.dataset.tooltipDuration || 300, 0],
        delay: document.body.dataset.tooltipDelay || 0,
        trigger: 'mouseenter focus click',
        touch: true,
        zIndex: 99999
      });
    }

    initialize() {
      this.descriptionScope.querySelectorAll('.rte > #vip-calculator').forEach((calculator) => calculator.remove());
      const descriptionRewards = {};
      ['cash', 'coins', 'vip_points'].forEach((type) => {
        const legacyId = type === 'vip_points' ? '#vipPointsAmount' : `#${type}Amount`;
        const element = this.descriptionScope.querySelector(`[data-deal-description-reward="${type}"], ${legacyId}`);
        const amount = Number(this.dataset[type === 'vip_points' ? 'baseVip' : `base${type[0].toUpperCase()}${type.slice(1)}`]) || 0;
        descriptionRewards[type] = {
          element,
          amount,
          html: element?.innerHTML || '',
          listItem: element?.closest('li'),
          visible: !element?.closest('li') || amount > 0
        };
        if (element) element.dataset.dealDescriptionReward = type;
      });
      this.originalValues = {
        baseCash: descriptionRewards.cash.amount,
        baseCoins: descriptionRewards.coins.amount,
        baseVipPoints: descriptionRewards.vip_points.amount,
        descriptionRewards
      };
      this.resetRewardAmounts();
      this.resetDescriptionRewards();
      this.setButtonMode('calculate');
      const storedUserId = this.readNumericStorage(VIP_STORAGE_KEY);
      if (storedUserId) {
        this.userIdInput.value = storedUserId;
        this.calculate({ userId: storedUserId, automatic: true, persist: false });
      }
    }

    formatNumber(number) {
      if (!Number.isFinite(Number(number))) return '0';
      const value = Number(number);
      if (value >= 1000000) return `${(value / 1000000).toFixed(2).replace(/\.?0+$/, '')}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(2).replace(/\.?0+$/, '')}k`;
      return value.toFixed(2).replace(/\.?0+$/, '');
    }

    formatDetailedNumber(number) {
      return Number.isFinite(Number(number)) ? Math.round(Number(number)).toLocaleString('en-US') : '0';
    }

    escapeHtml(value) {
      return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
      }[character]));
    }

    renderRewardAmount(element, amount, adjusted = false, shouldHighlight = false) {
      const type = element.dataset.dealRewardType;
      const prefix = ['cash', 'coins', 'token', 'vip_points'].includes(type) ? '' : 'x';
      const formatted = element.dataset.dealFormat === 'detailed'
        ? this.formatDetailedNumber(amount)
        : (type === 'vip_points' ? Number(amount).toLocaleString() : this.formatNumber(amount));
      if (type === 'cue') {
        const cueLabel = amount === 1 ? this.translations.fullCue : this.translations.fullCues;
        element.textContent = `${element.dataset.dealAmountPrefix || ''}x${formatted} (${cueLabel})`;
      } else {
        renderVipNumericValue(element, {
          prefix: `${element.dataset.dealAmountPrefix || ''}${prefix}`,
          value: formatted,
          highlighted: adjusted,
          animate: shouldHighlight
        });
      }
    }

    updateRewardAmounts(rewards, adjusted = false, shouldHighlight = false) {
      this.rewardScope?.querySelectorAll('[data-deal-reward-type]').forEach((element) => {
        const amount = rewards[element.dataset.dealRewardType];
        if (Number.isFinite(amount)) this.renderRewardAmount(element, amount, adjusted, shouldHighlight);
      });
    }

    updateDescriptionReward(type, amount, adjusted = false, shouldHighlight = false) {
      const reward = this.originalValues?.descriptionRewards[type];
      if (!reward?.element) return;
      renderVipNumericValue(reward.element, {
        prefix: type === 'vip_points' ? '+' : '',
        value: type === 'vip_points' ? Number(amount).toLocaleString() : this.formatNumber(amount),
        suffix: ` ${type === 'vip_points' ? this.translations.vipPoints : this.translations[type]}`,
        highlighted: adjusted,
        animate: shouldHighlight
      });
      if (reward.listItem && type !== 'vip_points') reward.listItem.style.display = amount > 0 ? 'list-item' : 'none';
    }

    updateProductTitle(rewards) {
      const type = this.dataset.dynamicTitleReward;
      if (!type || !this.productTitle || !this.originalProductTitle) return;
      const amount = Number(rewards?.[type]);
      if (!Number.isFinite(amount)) return;
      renderVipTitleAmount(this.productTitle, this.originalProductTitle, type, amount, true);
    }

    resetProductTitle() {
      if (this.productTitle && this.originalProductTitle) this.productTitle.textContent = this.originalProductTitle;
    }

    buildResult(data, baseValues, expanded) {
      const baseCash = Math.max(0, Number(baseValues.baseCash) || 0);
      const baseCoins = Math.max(0, Number(baseValues.baseCoins) || 0);
      const baseVipPoints = Math.max(0, Number(baseValues.baseVipPoints) || 0);
      const username = data.username || this.translations.player;
      const tier = this.escapeHtml(data.vip_tier || this.translations.notAvailable);
      const benefits = [];
      const benefitLine = ({ multiplier, label, bonus, total, detailed = false }) => {
        const format = detailed ? (value) => Number(value).toLocaleString() : (value) => this.formatNumber(value);
        return `<li class="vip-calculator__benefit">
          <strong class="vip-calculator__benefit-multiplier">${this.escapeHtml(multiplier)}x ${this.escapeHtml(label)}</strong>
          <span class="vip-calculator__benefit-divider" aria-hidden="true">|</span>
          <span class="vip-calculator__benefit-bonus">+${format(Math.max(0, bonus))}</span>
          <span class="vip-calculator__benefit-arrow" aria-hidden="true">→</span>
          <strong class="vip-calculator__benefit-total"><span data-vip-highlight-value>${this.formatDetailedNumber(total)}</span> ${this.escapeHtml(label)} ${this.escapeHtml(this.translations.total)}</strong>
        </li>`;
      };
      if (baseCash > 0 || data.cash > 0) benefits.push(benefitLine({
        multiplier: data.multipliers?.cash ?? this.translations.notAvailable,
        label: this.translations.cash,
        bonus: data.cash - baseCash,
        total: data.cash
      }));
      if (baseCoins > 0 || data.coins > 0) benefits.push(benefitLine({
        multiplier: data.multipliers?.coins ?? this.translations.notAvailable,
        label: this.translations.coins,
        bonus: data.coins - baseCoins,
        total: data.coins
      }));
      if (baseVipPoints > 0 || data.vip_points > 0) benefits.push(benefitLine({
        multiplier: data.multipliers?.vip_points ?? this.translations.notAvailable,
        label: this.translations.vipPoints,
        bonus: data.vip_points - baseVipPoints,
        total: data.vip_points,
        detailed: true
      }));
      return `<details class="vip-calculator__details"${expanded ? ' open' : ''}>
        <summary class="vip-calculator__summary">
          <span class="vip-calculator__welcome">${this.escapeHtml(this.translations.welcome.replace('__PLAYER__', username))}</span>
          <span class="vip-calculator__summary-separator" aria-hidden="true">|</span>
          <span class="vip-calculator__tier">${this.escapeHtml(this.translations.vipTierCompact)} <strong>${tier}</strong></span>
          <span class="vip-calculator__chevron" aria-hidden="true"></span>
        </summary>
        <div class="vip-calculator__benefits"><strong class="vip-calculator__benefits-title">${this.escapeHtml(this.translations.vipBenefits)}</strong><ul>${benefits.join('')}</ul></div>
      </details>`;
    }

    baseValues() {
      return this.originalValues || {
        baseCash: Number(this.dataset.baseCash) || 0,
        baseCoins: Number(this.dataset.baseCoins) || 0,
        baseVipPoints: Number(this.dataset.baseVip) || 0
      };
    }

    async request(userId, baseValues) {
      const response = await fetch(this.dataset.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          base_cash: Math.max(0, Number(baseValues.baseCash) || 0),
          base_coins: Math.max(0, Number(baseValues.baseCoins) || 0),
          base_vip_points: Math.max(0, Number(baseValues.baseVipPoints) || 0)
        })
      });
      let data = null;
      try { data = await response.json(); } catch (_) { data = null; }
      if (!response.ok) throw new Error(this.translations.calculationFailed);
      if (!data || !['cash', 'coins', 'vip_points'].every((key) => typeof data[key] === 'number')) {
        throw new Error(this.translations.invalidResponse);
      }
      return data;
    }

    async calculate({ userId: suppliedUserId, automatic = false, persist = true } = {}) {
      const userId = String(suppliedUserId || this.userIdInput.value).trim();
      if (!userId || !/^\d+$/.test(userId)) {
        this.error.textContent = userId ? this.translations.invalidUniqueId : this.translations.enterUniqueId;
        this.error.style.display = 'block';
        this.userIdInput.setAttribute('aria-invalid', 'true');
        if (automatic) this.removeStoredUserId();
        else this.userIdInput.focus();
        return;
      }
      this.userIdInput.removeAttribute('aria-invalid');
      this.userIdInput.value = userId;
      const sequence = ++this.requestSequence;
      this.mainButton.disabled = true;
      this.loading.style.display = 'flex';
      this.error.style.display = 'none';
      this.result.style.display = 'none';
      const baseValues = this.baseValues();
      try {
        const data = await this.request(userId, baseValues);
        if (sequence !== this.requestSequence) return;
        storeVipProfile(data, userId);
        ['cash', 'coins', 'vip_points'].forEach((type) => this.updateDescriptionReward(type, data[type], true, true));
        this.updateRewardAmounts(data, true, true);
        this.updateProductTitle(data);
        this.result.innerHTML = this.buildResult(data, baseValues, !automatic);
        this.result.querySelectorAll('[data-vip-highlight-value]').forEach((element) => highlightVipValue(element, true));
        this.result.style.display = 'block';
        this.activeUserId = userId;
        this.setButtonMode('reset');
        if (persist) this.storeUserId(userId);
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.error.textContent = error.message || this.translations.calculationFailed;
        this.error.style.display = 'block';
        console.error('Theme JS: Calculate Rewards Error:', error);
      } finally {
        if (sequence === this.requestSequence) {
          this.loading.style.display = 'none';
          this.mainButton.disabled = false;
        }
      }
    }

    clear({ keepUserId = false, clearStored = true, broadcast = true } = {}) {
      this.requestSequence += 1;
      this.activeUserId = '';
      if (!keepUserId) this.userIdInput.value = '';
      this.resetRewardAmounts();
      this.resetDescriptionRewards();
      this.resetProductTitle();
      this.result.style.display = 'none';
      this.result.innerHTML = '';
      this.error.style.display = 'none';
      this.loading.style.display = 'none';
      this.mainButton.disabled = false;
      this.setButtonMode('calculate');
      if (clearStored) this.removeStoredUserId(broadcast);
    }

    resetRewardAmounts() {
      this.rewardScope?.querySelectorAll('[data-deal-reward-type]').forEach((element) => {
        const amount = Number(element.dataset.baseAmount);
        if (Number.isFinite(amount)) this.renderRewardAmount(element, amount);
      });
    }

    resetDescriptionRewards() {
      Object.values(this.originalValues?.descriptionRewards || {}).forEach((reward) => {
        if (!reward.element) return;
        reward.element.innerHTML = reward.html;
        if (reward.listItem) reward.listItem.style.display = reward.visible ? 'list-item' : 'none';
      });
    }

    setButtonMode(mode) {
      this.mainButton.textContent = mode === 'reset' ? this.translations.reset : this.translations.calculate;
      this.mainButton.dataset.mode = mode;
    }

    readNumericStorage(key) {
      try {
        const value = window.localStorage.getItem(key)?.trim() || '';
        if (!value || /^\d+$/.test(value)) return value;
        window.localStorage.removeItem(key);
      } catch (_) {}
      return '';
    }

    storeUserId(userId) {
      try {
        window.localStorage.setItem(VIP_STORAGE_KEY, userId);
        const recentIds = [userId, ...this.readRecentIds().filter((id) => id !== userId)].slice(0, VIP_RECENT_LIMIT);
        window.localStorage.setItem(VIP_RECENT_STORAGE_KEY, JSON.stringify(recentIds));
      } catch (_) {}
      this.broadcast(userId);
    }

    removeStoredUserId(broadcast = true) {
      try { window.localStorage.removeItem(VIP_STORAGE_KEY); } catch (_) {}
      clearVipProfile();
      if (broadcast) this.broadcast('');
    }

    broadcast(userId) {
      document.dispatchEvent(new CustomEvent('eight-bp:vip-id-changed', { detail: { source: this.id, userId } }));
    }

    readRecentIds() {
      try {
        const stored = window.localStorage.getItem(VIP_RECENT_STORAGE_KEY)?.trim() || '';
        if (!stored) return [];
        const parsed = stored.startsWith('[') ? JSON.parse(stored) : [stored];
        if (!Array.isArray(parsed)) throw new Error('Invalid recent ID storage');
        const ids = Array.from(new Set(parsed.map(String).filter((id) => /^\d+$/.test(id))));
        if (ids.length !== parsed.length || ids.length > VIP_RECENT_LIMIT || !stored.startsWith('[')) {
          window.localStorage.setItem(VIP_RECENT_STORAGE_KEY, JSON.stringify(ids.slice(0, VIP_RECENT_LIMIT)));
        }
        return ids.slice(0, VIP_RECENT_LIMIT);
      } catch (_) {
        try { window.localStorage.removeItem(VIP_RECENT_STORAGE_KEY); } catch (_) {}
        return [];
      }
    }

    removeRecentId(userId) {
      try {
        const remaining = this.readRecentIds().filter((id) => id !== userId);
        if (remaining.length) window.localStorage.setItem(VIP_RECENT_STORAGE_KEY, JSON.stringify(remaining));
        else window.localStorage.removeItem(VIP_RECENT_STORAGE_KEY);
      } catch (_) {}
    }

    updateRecentSuggestion(show = false) {
      if (!this.recent || !this.recentItems) return;
      const currentValue = this.userIdInput.value.trim();
      const ids = this.readRecentIds().filter((id) => id !== currentValue);
      this.recentItems.innerHTML = ids.map((id) => `
        <span class="eight-bp-vip-calculator__recent-item">
          <button type="button" class="eight-bp-vip-calculator__recent-select" data-vip-recent-select="${id}"><strong>${id}</strong></button>
          <button type="button" class="eight-bp-vip-calculator__recent-dismiss" data-vip-recent-dismiss="${id}" aria-label="${this.escapeHtml(this.translations.close)}">×</button>
        </span>
      `).join('');
      this.recent.hidden = !show || ids.length === 0;
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('.calc-button')) {
        this.mainButton.dataset.mode === 'reset' ? this.clear() : this.calculate();
        return;
      }
      const recentSelect = target.closest('[data-vip-recent-select]');
      if (recentSelect) {
        const value = recentSelect.dataset.vipRecentSelect;
        if (value) {
          this.userIdInput.value = value;
          this.updateRecentSuggestion(false);
          this.calculate({ userId: value });
        }
        return;
      }
      const recentDismiss = target.closest('[data-vip-recent-dismiss]');
      if (recentDismiss) {
        this.removeRecentId(recentDismiss.dataset.vipRecentDismiss);
        this.updateRecentSuggestion(true);
        this.userIdInput.focus();
        return;
      }
      const trigger = target.closest('[data-vip-club-trigger]');
      if (trigger && !this.closest('eight-bp-bundle-details')) {
        const modal = document.getElementById(trigger.getAttribute('aria-controls'));
        if (modal?.matches('[data-vip-club-modal]')) {
          event.preventDefault();
          this.openModal(modal, trigger);
        }
      }
    }

    onInput() {
      this.userIdInput.removeAttribute('aria-invalid');
      this.error.style.display = 'none';
      const value = this.userIdInput.value.trim();
      this.updateRecentSuggestion(document.activeElement === this.userIdInput);
      if (!value) this.clear();
      else if (this.activeUserId && value !== this.activeUserId) this.clear({ keepUserId: true, clearStored: false });
    }

    onFocus() { this.updateRecentSuggestion(true); }
    onBlur() { window.setTimeout(() => this.updateRecentSuggestion(false), 120); }
    onSubmit(event) { event.preventDefault(); this.mainButton.click(); }

    onExternalId(event) {
      if (event.detail?.source === this.id) return;
      this.applyExternalId(event.detail?.userId || '');
    }

    onStorage(event) {
      if (event.key === VIP_RECENT_STORAGE_KEY) this.updateRecentSuggestion(document.activeElement === this.userIdInput);
      else if (event.key === VIP_STORAGE_KEY) this.applyExternalId(event.newValue?.trim() || '');
    }

    applyExternalId(userId) {
      if (!userId) return this.clear({ clearStored: false, broadcast: false });
      if (!/^\d+$/.test(userId) || userId === this.activeUserId) return;
      this.userIdInput.value = userId;
      this.calculate({ userId, automatic: true, persist: false });
    }

    openModal(modal, trigger) {
      this.activeModal = modal;
      this.modalTrigger = trigger;
      if (modal.parentNode !== document.body) {
        this.modalPlaceholder = document.createComment('8bp-vip-modal');
        modal.before(this.modalPlaceholder);
        document.body.appendChild(modal);
      }
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.querySelector('[data-vip-club-close]')?.focus();
    }

    closeModal(restoreFocus = true) {
      if (!this.activeModal) return;
      this.activeModal.hidden = true;
      this.activeModal.setAttribute('aria-hidden', 'true');
      if (this.modalPlaceholder?.parentNode) this.modalPlaceholder.replaceWith(this.activeModal);
      const trigger = this.modalTrigger;
      this.activeModal = null;
      this.modalPlaceholder = null;
      this.modalTrigger = null;
      if (restoreFocus) trigger?.focus();
    }

    onDocumentClick(event) {
      if (!this.activeModal) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-vip-club-close]') || target === this.activeModal) {
        event.preventDefault();
        event.stopPropagation();
        this.closeModal();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.activeModal) {
        event.preventDefault();
        event.stopPropagation();
        this.closeModal();
      }
    }
  }

  class EightBpBundleDetails extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bundleDetailsReady === 'true') return;
      this.dataset.bundleDetailsReady = 'true';
      this.vipModalId = this.querySelector('[data-vip-club-modal]')?.id || '';
      this.activeVipModal = null;
      this.activeVipTrigger = null;
      this.vipModalPlaceholder = null;
      this.cueModalIds = new Set(Array.from(this.querySelectorAll('[data-cue-card-modal][id]'), (modal) => modal.id));
      this.activeCueModal = null;
      this.activeCueTrigger = null;
      this.cueModalPlaceholder = null;
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.addEventListener('click', this.onTriggerClick, true);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeyDown, true);
    }

    disconnectedCallback() {
      if (this.activeCueModal) this.closeCueCard(this.activeCueModal, false);
      if (this.activeVipModal) this.closeVipClub(this.activeVipModal, false);
      this.removeEventListener('click', this.onTriggerClick, true);
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeyDown, true);
    }

    onTriggerClick(event) {
      const trigger = event.target.closest('[data-bundle-trigger-index]');
      if (!trigger || !this.contains(trigger)) return;

      const index = trigger.dataset.bundleTriggerIndex;
      const panel = this.querySelector('[data-bundle-details-panel]');
      if (!panel) return;

      panel.querySelectorAll('[data-bundle-detail-index]').forEach((detail) => {
        const isSelected = detail.dataset.bundleDetailIndex === index;
        detail.classList.toggle('is-selected', isSelected);
        if (detail.tagName === 'DETAILS') detail.open = isSelected;
      });

      window.setTimeout(() => {
        const selected = panel.querySelector(`[data-bundle-detail-index="${CSS.escape(index)}"]`);
        panel.scrollTop = selected && index !== '0' ? Math.max(0, selected.offsetTop - 120) : 0;
        selected?.querySelectorAll('eight-bp-animation').forEach((animation) => initialize(animation));
      }, 420);
    }

    onDocumentClick(event) {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      if (!target) return;

      const cueTrigger = target.closest('[data-cue-card-trigger]');
      if (cueTrigger) {
        const modalId = cueTrigger.getAttribute('aria-controls');
        const modal = modalId ? document.getElementById(modalId) : null;
        if (!this.ownsCueModal(modal)) return;
        event.preventDefault();
        event.stopPropagation();
        this.activeCueTrigger = cueTrigger;
        this.openCueCard(modal);
        return;
      }

      const cueClose = target.closest('[data-cue-card-close]');
      if (cueClose) {
        const modal = cueClose.closest('[data-cue-card-modal]');
        if (!this.ownsCueModal(modal)) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeCueCard(modal);
        return;
      }

      const cueModal = target.closest('[data-cue-card-modal]');
      if (cueModal && target === cueModal && this.ownsCueModal(cueModal)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeCueCard(cueModal);
        return;
      }

      const vipTrigger = target.closest('[data-vip-club-trigger]');
      if (vipTrigger) {
        const modal = this.findVipModal(vipTrigger);
        if (!modal) return;
        event.preventDefault();
        event.stopPropagation();
        this.activeVipTrigger = vipTrigger;
        this.openVipClub(modal);
        return;
      }

      const vipClose = target.closest('[data-vip-club-close]');
      if (vipClose) {
        const modal = vipClose.closest('[data-vip-club-modal]');
        if (!this.ownsVipModal(modal)) return;
        event.preventDefault();
        event.stopPropagation();
        this.closeVipClub(modal);
        return;
      }

      const vipModal = target.closest('[data-vip-club-modal]');
      if (vipModal && target === vipModal && this.ownsVipModal(vipModal)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeVipClub(vipModal);
      }
    }

    onKeyDown(event) {
      const target = event.target instanceof Element ? event.target : null;
      const cueTrigger = target?.closest('[data-cue-card-trigger]');
      if ((event.key === 'Enter' || event.key === ' ') && cueTrigger) {
        const modalId = cueTrigger.getAttribute('aria-controls');
        const modal = modalId ? document.getElementById(modalId) : null;
        if (this.ownsCueModal(modal)) {
          event.preventDefault();
          event.stopPropagation();
          this.activeCueTrigger = cueTrigger;
          this.openCueCard(modal);
        }
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.activeCueModal && !this.activeCueModal.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.closeCueCard(this.activeCueModal);
        return;
      }
      const modal = this.activeVipModal && !this.activeVipModal.hidden
        ? this.activeVipModal
        : document.getElementById(this.vipModalId);
      if (this.ownsVipModal(modal) && !modal.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.closeVipClub(modal);
      }
    }

    findVipModal(trigger) {
      const panel = trigger.closest('[data-bundle-details-panel]');
      const panelModal = panel?.querySelector('[data-vip-club-modal]');
      if (this.ownsVipModal(panelModal)) return panelModal;

      const controlsId = trigger.getAttribute('aria-controls');
      const referencedModal = controlsId ? document.getElementById(controlsId) : null;
      return this.ownsVipModal(referencedModal) ? referencedModal : null;
    }

    ownsVipModal(modal) {
      return Boolean(this.vipModalId && modal && modal.matches('[data-vip-club-modal]') && modal.id === this.vipModalId);
    }

    ownsCueModal(modal) {
      return Boolean(modal?.matches('[data-cue-card-modal]') && this.cueModalIds.has(modal.id));
    }

    openCueCard(modal) {
      if (!this.ownsCueModal(modal)) return;
      this.activeCueModal = modal;
      if (modal.parentNode !== document.body) {
        this.cueModalPlaceholder = document.createComment('8bp-cue-card-modal');
        modal.before(this.cueModalPlaceholder);
        document.body.appendChild(modal);
      }
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.querySelector('[data-cue-card-close]')?.focus();
    }

    closeCueCard(modal = this.activeCueModal, restoreFocus = true) {
      if (!this.ownsCueModal(modal)) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      if (this.cueModalPlaceholder?.parentNode) this.cueModalPlaceholder.replaceWith(modal);
      const trigger = this.activeCueTrigger;
      this.activeCueModal = null;
      this.activeCueTrigger = null;
      this.cueModalPlaceholder = null;
      if (restoreFocus) trigger?.focus();
    }

    openVipClub(modal) {
      if (!modal) return;
      this.activeVipModal = modal;
      if (modal.parentNode !== document.body) {
        this.vipModalPlaceholder = document.createComment('8bp-vip-modal');
        modal.before(this.vipModalPlaceholder);
        document.body.appendChild(modal);
      }
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.querySelector('[data-vip-club-close]')?.focus();
    }

    closeVipClub(modal = this.activeVipModal, restoreFocus = true) {
      if (!this.ownsVipModal(modal)) return;
      if (!modal) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      const trigger = this.activeVipTrigger?.isConnected
        ? this.activeVipTrigger
        : document.querySelector(`[data-vip-club-trigger][aria-controls="${CSS.escape(this.vipModalId)}"]`);
      if (this.vipModalPlaceholder?.parentNode) {
        this.vipModalPlaceholder.replaceWith(modal);
      }
      this.vipModalPlaceholder = null;
      this.activeVipModal = null;
      this.activeVipTrigger = null;
      if (restoreFocus) trigger?.focus();
    }
  }

  if (!customElements.get('eight-bp-animation')) {
    customElements.define('eight-bp-animation', EightBpAnimation);
  }
  if (!customElements.get('eight-bp-card-slider')) {
    customElements.define('eight-bp-card-slider', EightBpCardSlider);
  }
  if (!customElements.get('eight-bp-bundle-details')) {
    customElements.define('eight-bp-bundle-details', EightBpBundleDetails);
  }
  if (!customElements.get('eight-bp-vip-calculator')) {
    customElements.define('eight-bp-vip-calculator', EightBpVipCalculator);
  }
  document.addEventListener('visibilitychange', () => {
    records.forEach((record) => {
      if (record.player) record.player.paused = document.hidden || !record.visible;
    });
  });

})();
